import React, { useState, useEffect } from "react";
import { T } from "../../app/tokens.js";
import { Modal } from "../../components/Modal.jsx";
import { getProjects } from "../../data/projectsClient.js";

const formRowStyle = {
  display: "flex", flexDirection: "column", gap: 6, marginBottom: 16,
};
const labelStyle = {
  fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark,
};
const inputStyle = {
  padding: "8px 12px", fontSize: T.fsBase, color: T.dark,
  background: T.white, border: `1px solid ${T.borderDark}`, borderRadius: 6,
};
const selectStyle = { ...inputStyle, appearance: "none" };

const buttonContainerStyle = {
  display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24, paddingTop: 16,
  borderTop: `1px solid ${T.border}`,
};
const primaryButtonStyle = {
  padding: "8px 16px", borderRadius: 6, fontSize: T.fsSm5, fontWeight: T.fwSemibold,
  background: T.accent, color: T.white, border: "none", cursor: "pointer",
};
const secondaryButtonStyle = {
  padding: "8px 16px", borderRadius: 6, fontSize: T.fsSm5, fontWeight: T.fwSemibold,
  background: "transparent", color: T.mid, border: "none", cursor: "pointer",
};

export function ObjectiveForm({ isOpen, onClose, initialData, selectedYear, onSubmit }) {
  const [name, setName] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [status, setStatus] = useState("on_track");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name || "");
      setYear(initialData?.fiscalYear || selectedYear || new Date().getUTCFullYear());
      setStatus(initialData?.status || "on_track");
    }
  }, [isOpen, initialData, selectedYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({ name, fiscalYear: Number(year), status });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={initialData ? "Edit Objective" : "New Objective"} maxWidth={500}>
      <form onSubmit={handleSubmit} style={{ padding: 20 }}>
        <div style={formRowStyle}>
          <label style={labelStyle}>Objective Name</label>
          <input required style={inputStyle} value={name} onChange={e => setName(e.target.value)} autoFocus />
        </div>
        <div style={formRowStyle}>
          <label style={labelStyle}>Year</label>
          <input required type="number" style={inputStyle} value={year} onChange={e => setYear(e.target.value)} />
        </div>
        <div style={formRowStyle}>
          <label style={labelStyle}>Status</label>
          <select style={selectStyle} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="on_track">On Track</option>
            <option value="at_risk">At Risk</option>
            <option value="off_track">Off Track</option>
          </select>
        </div>
        <div style={buttonContainerStyle}>
          <button type="button" onClick={onClose} style={secondaryButtonStyle} disabled={loading} data-testid="button-cancel-objective">Cancel</button>
          <button type="submit" style={primaryButtonStyle} disabled={loading} data-testid="button-submit-objective">{loading ? "Saving..." : "Save Objective"}</button>
        </div>
      </form>
    </Modal>
  );
}

export function KeyResultForm({ isOpen, onClose, initialData, objectiveId, onSubmit }) {
  const [title, setTitle] = useState("");
  const [quarter, setQuarter] = useState("Q1");
  const [targetType, setTargetType] = useState("percent");
  const [targetValue, setTargetValue] = useState(100);
  const [targetDate, setTargetDate] = useState("");
  const [status, setStatus] = useState("on_track");
  const [manualPercent, setManualPercent] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle(initialData?.title || "");
      setQuarter(initialData?.quarter || "Q1");
      setTargetType(initialData?.targetType || "percent");
      setTargetValue(initialData?.targetValue || 100);
      setTargetDate(initialData?.targetDate || "");
      setStatus(initialData?.status || "on_track");
      setManualPercent(initialData?.manualPercent || 0);
    }
  }, [isOpen, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({
        objectiveId, title, quarter, targetType, targetValue: Number(targetValue),
        targetDate: targetDate || null, status, manualPercent: Number(manualPercent)
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={initialData ? "Edit Key Result" : "New Key Result"} maxWidth={500}>
      <form onSubmit={handleSubmit} style={{ padding: 20 }}>
        <div style={formRowStyle}>
          <label style={labelStyle}>Title</label>
          <input required style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} autoFocus />
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <div style={{ ...formRowStyle, flex: 1, marginBottom: 0 }}>
            <label style={labelStyle}>Quarter</label>
            <select style={selectStyle} value={quarter} onChange={e => setQuarter(e.target.value)}>
              <option value="Q1">Q1</option>
              <option value="Q2">Q2</option>
              <option value="Q3">Q3</option>
              <option value="Q4">Q4</option>
            </select>
          </div>
          <div style={{ ...formRowStyle, flex: 1, marginBottom: 0 }}>
            <label style={labelStyle}>Status</label>
            <select style={selectStyle} value={status} onChange={e => setStatus(e.target.value)}>
              <option value="on_track">On Track</option>
              <option value="at_risk">At Risk</option>
              <option value="off_track">Off Track</option>
            </select>
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <div style={{ ...formRowStyle, flex: 1, marginBottom: 0 }}>
            <label style={labelStyle}>Target Type</label>
            <select style={selectStyle} value={targetType} onChange={e => setTargetType(e.target.value)}>
            <option value="percent">Percentage</option>
              <option value="number">Number</option>
              <option value="currency">Currency</option>
            <option value="binary">Boolean</option>
            </select>
          </div>
          <div style={{ ...formRowStyle, flex: 1, marginBottom: 0 }}>
            <label style={labelStyle}>Target Value</label>
            <input required type="number" style={inputStyle} value={targetValue} onChange={e => setTargetValue(e.target.value)} />
          </div>
        </div>
        <div style={formRowStyle}>
          <label style={labelStyle}>Target Date</label>
          <input type="date" style={inputStyle} value={targetDate} onChange={e => setTargetDate(e.target.value)} />
        </div>
        <div style={formRowStyle}>
          <label style={labelStyle}>Manual Progress %</label>
          <input required type="number" min={0} max={100} style={inputStyle} value={manualPercent} onChange={e => setManualPercent(e.target.value)} />
        </div>
        <div style={buttonContainerStyle}>
          <button type="button" onClick={onClose} style={secondaryButtonStyle} disabled={loading} data-testid="button-cancel-kr">Cancel</button>
          <button type="submit" style={primaryButtonStyle} disabled={loading} data-testid="button-submit-kr">{loading ? "Saving..." : "Save Key Result"}</button>
        </div>
      </form>
    </Modal>
  );
}

export function InitiativeForm({ isOpen, onClose, initialData, keyResultId, onSubmit }) {
  const [title, setTitle] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [status, setStatus] = useState("on_track");
  const [percentOverride, setPercentOverride] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);
  const [projectLoadError, setProjectLoadError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle(initialData?.title || "");
      setOwnerId(initialData?.ownerId || "");
      setStatus(initialData?.status || "on_track");
      setPercentOverride(initialData?.percentOverride !== null && initialData?.percentOverride !== undefined ? initialData.percentOverride : "");
      setProjectId(initialData?.projectId || "");
      setProjectLoadError(null);
      getProjects()
        .then((items) => setProjects(Array.isArray(items) ? items : []))
        .catch((error) => setProjectLoadError(error));
    }
  }, [isOpen, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({
        keyResultId, title, ownerId: ownerId || null, status,
        projectId: projectId || null,
        percentOverride: percentOverride === "" ? null : Number(percentOverride)
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const isLinked = !!projectId;
  const liveProjectName = initialData?.projectName || projects.find((project) => String(project.id) === String(projectId))?.name;
  const linkedProject = projects.find((project) => String(project.id) === String(projectId));
  const liveProjectOwner = initialData?.projectOwner || linkedProject?.internalOwner;
  const liveProjectStatus = initialData?.projectStatus || linkedProject?.statusOverall;
  const liveProjectPhase = initialData?.projectPhase || linkedProject?.phase;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={initialData ? "Edit Initiative" : "New Initiative"} maxWidth={500}>
      <form onSubmit={handleSubmit} style={{ padding: 20 }}>
        {isLinked && (
          <div style={{ padding: 12, background: T.blueSoft, borderRadius: 6, marginBottom: 16, fontSize: T.fsSm, color: T.blue }}>
            This initiative is linked to project <strong>{liveProjectName || projectId}</strong>. Project name, owner, status, and phase are read from the live Project record.
          </div>
        )}
        <div style={formRowStyle}>
          <label style={labelStyle}>Linked Project</label>
          <select style={selectStyle} value={projectId} onChange={e => setProjectId(e.target.value)}>
            <option value="">No linked project</option>
            {projectId && !projects.some((project) => String(project.id) === String(projectId)) && (
              <option value={projectId}>{liveProjectName || projectId}</option>
            )}
            {projects.map((project) => (
              <option key={project.id} value={project.id}>{project.name || project.title || project.id}</option>
            ))}
          </select>
          {projectLoadError && <span style={{ fontSize: T.fsXs, color: T.red }}>Projects could not be loaded. You can still remove an existing link.</span>}
          {isLinked && linkedProject?.phase && (
            <span style={{ fontSize: T.fsXs, color: T.mid }}>
              Live phase: {String(linkedProject.phase).replaceAll("_", " ")}
            </span>
          )}
        </div>
        <div style={formRowStyle}>
          <label style={labelStyle}>Initiative title</label>
          <input required style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} autoFocus={!isLinked} />
        </div>
        <div style={formRowStyle}>
          <label style={labelStyle}>Initiative owner (access)</label>
          <input style={inputStyle} value={ownerId} onChange={e => setOwnerId(e.target.value)} disabled={isLinked} placeholder="User UUID (optional)" />
        </div>
        <div style={formRowStyle}>
          <label style={labelStyle}>{isLinked ? "Initiative status" : "Status"}</label>
          <select style={selectStyle} value={status} onChange={e => setStatus(e.target.value)} disabled={isLinked}>
            <option value="on_track">On Track</option>
            <option value="at_risk">At Risk</option>
            <option value="off_track">Off Track</option>
          </select>
        </div>
        {isLinked && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: 12, background: T.tile, borderRadius: 6, marginBottom: 16 }}>
            <div><div style={{ ...labelStyle, fontSize: T.fsXs }}>Live project name</div><div style={{ fontSize: T.fsSm, color: T.dark }}>{liveProjectName || "—"}</div></div>
            <div><div style={{ ...labelStyle, fontSize: T.fsXs }}>Live project owner</div><div style={{ fontSize: T.fsSm, color: T.dark }}>{liveProjectOwner || "—"}</div></div>
            <div><div style={{ ...labelStyle, fontSize: T.fsXs }}>Live project status</div><div style={{ fontSize: T.fsSm, color: T.dark }}>{liveProjectStatus || "—"}</div></div>
            <div><div style={{ ...labelStyle, fontSize: T.fsXs }}>Live project phase</div><div style={{ fontSize: T.fsSm, color: T.dark }}>{liveProjectPhase?.replaceAll("_", " ") || "—"}</div></div>
          </div>
        )}
        <div style={formRowStyle}>
          <label style={labelStyle}>Progress % Override (Optional)</label>
          <input type="number" min={0} max={100} style={inputStyle} value={percentOverride} onChange={e => setPercentOverride(e.target.value)} placeholder="e.g. 50" />
        </div>
        <div style={buttonContainerStyle}>
          <button type="button" onClick={onClose} style={secondaryButtonStyle} disabled={loading} data-testid="button-cancel-initiative">Cancel</button>
          <button type="submit" style={primaryButtonStyle} disabled={loading} data-testid="button-submit-initiative">{loading ? "Saving..." : "Save Initiative"}</button>
        </div>
      </form>
    </Modal>
  );
}
